import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Maximize2Icon, MousePointerClickIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { EmbedWindow } from '../EmbedWindow';
import { BumblebeePath } from '../BumblebeePath';
import { ivfPulseUrl } from '../../../data/deck';

/** The bespoke reveal: the IVF Pulse, developed for Ferring. */
export function CongressSection() {
  const [fullOpen, setFullOpen] = useState(false);
  return <Section id="congress" data-id="element-428" data-mp="1ngtl0qoif9sc">
      <AnimatePresence data-id="element-429" data-mp="1nfiieu6sw82t">
        {fullOpen && <EmbedWindow key="ivf-pulse" url={ivfPulseUrl} title="The IVF Pulse — ESHRE 2027 campaign concept" onClose={() => setFullOpen(false)} data-id="element-430" data-mp="225hg6vug1hhr" />}
      </AnimatePresence>

      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-16" data-id="element-431" data-mp="2b6fjfbx25kza">
        <InView data-id="element-432" data-mp="275c72ze7y72t">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-[var(--fg)] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-433" data-mp="275lvwjnj286x">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E8A92E]" aria-hidden="true" data-id="element-434" data-mp="3kd1tm2pji6hn" />
            Developed for Ferring · ESHRE 2027 proposal
          </span>
          <h2 className="text-[46px] font-extrabold leading-[0.92] tracking-[-0.05em] text-[var(--fg)] sm:text-[68px] xl:text-[88px]" data-id="element-435" data-mp="3ubzn7k1toq7i">
            Introducing
            <span className="block text-[var(--brand)]" data-id="element-436" data-mp="356n6jirmx4k">the IVF Pulse.</span>
          </h2>
          <p className="mt-6 text-[22px] font-bold leading-snug tracking-[-0.02em] text-[var(--fg)] sm:text-[26px]" data-id="element-437" data-mp="75ywfcrn120u">
            A conversation that starts before ESHRE — and continues long after.
          </p>
        </InView>
        <InView delay={0.1} data-id="element-438" data-mp="gz7dcovnjbtb">
          <p className="text-[16px] leading-[1.7] text-[var(--muted)]" data-id="element-439" data-mp="lwfx2twg7nwo">
            What if Ferring could engage fertility specialists before the congress, make every interaction more valuable
            during ESHRE, and turn the collective insights into a reason to reconnect afterwards?
          </p>
        </InView>
      </div>

      <InView delay={0.16} className="mt-12" data-id="element-440" data-mp="3sehhjdd5u9ix">
        <div className="rounded-[22px] bg-white px-6 pb-5 pt-4 ring-1 ring-[var(--fg)]/[0.06]" data-id="element-441" data-mp="3sblkrsd1tqw2">
          <BumblebeePath data-id="element-442" data-mp="ayrk5j2ol3hd" />
          <p className="mt-3 text-center text-[13px] leading-relaxed text-[var(--muted)]" data-id="element-443" data-mp="65on30yp3bwf">
            Like a bee between flowers, Ferring connects specialists, gathers perspectives — and brings collective insight to
            the next conversation.
          </p>
        </div>
      </InView>

      <InView delay={0.22} y={26} className="mt-6" data-id="element-444" data-mp="tvixlbvjkodi">
        <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_50px_90px_-60px_rgba(14,27,51,0.6)] ring-1 ring-[var(--fg)]/10" data-id="element-445" data-mp="tv15mrfwwwbe">
          <div className="flex items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-446" data-mp="2vmw23ztlku91">
            <div className="flex gap-1.5" aria-hidden="true" data-id="element-447" data-mp="2qocdfrnmd4cz">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-448" data-mp="2qpx8diduipbd" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-449" data-mp="167cxnxrnqk1v" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-450" data-mp="1b5egqip32jdc" />
            </div>
            <span className="hidden items-center gap-1.5 truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)] sm:inline-flex" data-id="element-451" data-mp="wgqr0t1x53vb">
              <MousePointerClickIcon className="h-3 w-3 text-[var(--brand)]" aria-hidden="true" data-id="element-452" data-mp="nfk687s8l6w9" />
              Live campaign prototype — click a device to explore each moment
            </span>
            <button type="button" onClick={() => setFullOpen(true)} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-[var(--fg)] px-3.5 text-[11px] font-bold text-white transition-colors hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-453" data-mp="ret15fz0jate">
              <Maximize2Icon className="h-3 w-3" aria-hidden="true" data-id="element-454" data-mp="2cs4gnwmhnzwn" />
              Full screen
            </button>
          </div>
          <iframe src={ivfPulseUrl} title="The IVF Pulse — ESHRE 2027 connected congress campaign" className="block h-[min(82vh,820px)] min-h-[560px] w-full border-0 bg-white" allow="autoplay; fullscreen" data-id="element-455" data-mp="7smmk2uuhj9t" />
        </div>
      </InView>

      <p className="mt-4 text-[11px] leading-relaxed text-[var(--muted)]" data-id="element-456" data-mp="3pyhhehrq8ysl">
        Campaign proposal for discussion. Peer figures are illustrative, and all medical and product content is subject to
        Ferring medical, legal and regulatory review.
      </p>
    </Section>;
}